// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows out side swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 *
 *
 */

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import ArrowNavigation from "@flip/App/Components/XngCarousel/ArrowNavigation";
import { SwiperContext } from "@flip/App/Components/XngCarousel/XngCarousel.context";
import { xSlidesPerView } from "@flip/App/Components/XngCarousel/XngCarousel.helper";
import { SwiperInstance, XSwiper } from "@flip/App/Components/XngCarousel/XngCarousel.models";
import { xSwiperStyle } from "@flip/App/Components/XngCarousel/XngCarousel.style";
import { color } from "@flip/XDL/Utilities";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import cloneDeep from "lodash/cloneDeep";
import omit from "lodash/omit"
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = Swiper & {
    /** callback when xSlidesChange */
    onSlidesPerView?: (slidesPerView?: number) => void;
    /**
     * To enable xSlidesPerView:
     * 1. add xSlidesPerView: true
     * 2. don't pass slidesPerView and breakpoint to XngCarousel
     *  */
    xSlidesPerView?: boolean;
    /** max number of slides perView for all screens */
    maximumNumOfSlidesPerView?: number;
    extendedStyles?: SwiperStyle;
    navigationOutSide?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            &.navigation-outside {
                display: flex;
                align-items: center;
                position: relative;
                width: 100%;
                margin: auto;

                .arrow-button {
                    border: 1px solid ${color("tertiary")};
                    border-radius: 16px;
                    height: 16px;
                    width: 16px;
                    flex: 0 0 16px;
                    ${IconWrapper} {
                        justify-content: center;
                    }
                    svg {
                        height: 8px;
                        width: 4px;
                    }
                }
            }

            .swiper-container {
                width: 100%;
            }

            .xng-pagination-container {
                margin-top: 32px;
                &,
                > div {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                > div:not(:empty) {
                    border: 1px solid ${color("tertiary")};
                    border-radius: 16px;
                    padding: 8px;
                    height: 32px;
                    min-width: 122px;
                }
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                flex: 1;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 0 4px;
                    border: 1px solid ${color("common.ui4")};
                    background: ${color("common.white")};
                    border-radius: 50%;
                    opacity: 1;

                    &:hover,
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui12")};
                        border-color: ${color("common.ui12")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        maximumNumOfSlidesPerView,
        xSlidesPerView: xSlidesPerViewEnable,
        onSlidesPerView,
        extendedStyles,
        navigationOutSide,
        ...otherProps
    } = props;

    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));
    const [singlePage, setSinglePage] = useState(false);
    const [hide, setHide] = useState(false);

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            await require("swiper/swiper-bundle.min.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    const paginationId = `parent-carousel-pagination-${uniqueId()}`;
    useEffect(() => {
        const parentDiv = document.getElementById(paginationId);
        const hasHiddenChild =
            parentDiv &&
            Array.from(parentDiv.children).some(child => {
                return getComputedStyle(child).display === "none";
            });

        if (hasHiddenChild) {
            setHide(true);
        }
    });

    /**
     * call when swiper created
     * @param swiper
     */
    const initSwiper = (swiper: XSwiper) => {
        setSwiperInstance(swiper);
        xSlidesPerView({
            swiper,
            maximumNumOfSlidesPerView,
            onSlidesPerView,
            enable: !(otherProps.slidesPerView || otherProps.breakpoints) && xSlidesPerViewEnable,
        });

        // Add "position: relative" for element before carousel wrapper
        if (swiper.el.parentElement?.parentElement) {
            swiper.el.parentElement.parentElement.style.position = "relative";
        }
    };

    if (!isSwiperCssImported) {
        return null;
    }

    return (
        <SwiperContext.Provider value={swiperInstance}>
            <StyledWrapper
                {...styles.container}
                className={`carousel-main-wrapper ${navigationOutSide ? "navigation-outside" : ""}  ${className || ""}`}
            >
                {otherProps.navigation && navigationOutSide && <ArrowNavigation arrowType="prev" showOnMobile={true} />}
                <Swiper
                    {...omit(otherProps, ["loop"])}
                    onSwiper={swiper => {
                        initSwiper(swiper);
                        otherProps.onSwiper?.(swiper);
                        setSinglePage(swiper.isBeginning && swiper.isEnd);
                        setTimeout(() => {
                            window.dispatchEvent(new Event("resize"));
                        }, 500);
                    }}
                    onSlideChange={swiper => {
                        otherProps.onSlideChange?.(swiper);
                    }}
                    onResize={swiper => {
                        otherProps.onResize?.(swiper);
                        setSinglePage(swiper.isBeginning && swiper.isEnd);
                    }}
                >
                    {React.Children.map(children, child => {
                        // for null case
                        if (!isValidElement(child)) {
                            return;
                        }

                        const { props } = child;
                        const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                        const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                        const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                        return (
                            <SwiperSlide {...swiperSlideProps}>
                                {React.createElement(child.type, childrenProps)}
                            </SwiperSlide>
                        );
                    })}

                    {!singlePage && (otherProps.pagination || otherProps.navigation) && !navigationOutSide && !hide && (
                        <StyledWrapper className="xng-pagination-container">
                            <StyledWrapper id={paginationId}>
                                {otherProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} loop={otherProps.loop} />}
                                {otherProps.pagination && <div className="swiper-pagination" />}
                                {otherProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={true} loop={otherProps.loop} />}
                                <StyledWrapper className="xng-pagination-block"></StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {otherProps.scrollbar && <div className="swiper-scrollbar" />}
                </Swiper>
                {otherProps.navigation && navigationOutSide && <ArrowNavigation arrowType="next" showOnMobile={true} />}
            </StyledWrapper>
        </SwiperContext.Provider>
    );
};

XngCarousel.defaultProps = {
    xSlidesPerView: false,
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: { clickable: true, el: ".swiper-pagination" },
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
    navigationOutSide: false,
    loop: false
};

export default XngCarousel;
